import { forwardRef, ReactNode } from "react";
import Link from "next/link";
import styles from "./Button.module.css";
import Spinner from "./Spinner";

export type ButtonVariant = "outlined" | "contained" | "text" | "square";
export type ButtonColor =
  | "primary"
  | "secondary"
  | "error"
  | "default"
  | "white";
export type ButtonSize = "xsmall" | "small" | "medium" | "large" | "xlarge";

interface ButtonProps {
  label?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
  href?: string;
  type?: "button" | "submit" | "reset";
  fullWidth?: boolean;
  /** Use native anchor tag instead of Next.js Link (for auth redirects) */
  nativeLink?: boolean;
  /** Target for link (e.g., "_blank" for new tab) */
  target?: string;
  /** Additional CSS class name */
  className?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    label,
    startIcon,
    endIcon,
    variant = "contained",
    color = "default",
    size = "medium",
    disabled,
    loading,
    onClick,
    href,
    type = "button",
    fullWidth = false,
    nativeLink = false,
    target,
    className,
  }: ButtonProps,
  ref,
) {
  const iconButtonLoading = loading && !label;

  const classNames = [
    styles.button,
    styles[variant],
    styles[color],
    styles[size],
    fullWidth ? styles.fullWidth : "",
    disabled ? styles.disabled : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {startIcon && !iconButtonLoading && startIcon}
      {label && <span className={styles.label}>{label}</span>}
      {endIcon && !iconButtonLoading && endIcon}
      {loading && <Spinner />}
    </>
  );

  if (href && !disabled) {
    // Use native anchor for auth redirects, external links, or when target is specified
    if (nativeLink || target) {
      return (
        <a
          href={href}
          className={classNames}
          onClick={onClick}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
        >
          {content}
        </a>
      );
    }
    return (
      <Link href={href} className={classNames} onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <button
      ref={ref}
      className={classNames}
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
    >
      {content}
    </button>
  );
});

export default Button;
